/* --------------------------------------------------------------------------
    PREMIUM AGENCY REDESIGN (SEO OPTIMIZED)
--------------------------------------------------------------------------- */

import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet"; // IMPORT THIS
import {
  motion,
  useInView,
  useSpring,
  useMotionValue,
  AnimatePresence
} from "framer-motion";
import {
  ArrowUpRight,
  Video,
  MonitorPlay,
  Share2,
  Globe,
  Briefcase,
  Users,
  Trophy,
  Plus,
} from "lucide-react";

import Carousel from "./Components/Carousel";
import Partners from "./Components/Partners";

/* --------------------------------------------------------------------------
    THEME CONFIGURATION
--------------------------------------------------------------------------- */
const THEME = {
  bg: "bg-[#F3F2ED]", 
  textMain: "text-[#0F0F0F]", 
  textMuted: "text-[#6B6A65]", 
  border: "border-[#0F0F0F]/10", 
  accent: "text-[#0F0F0F]", 
  buttonPrimary: "bg-[#0F0F0F] text-[#F3F2ED]",
};

/* --------------------------------------------------------------------------
    ANIMATIONS
--------------------------------------------------------------------------- */
const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }} 
    className={className}
  >
    {children}
  </motion.div>
);

const StaggerContainer = ({ children, className = "" }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={{
      hidden: {},
      visible: { transition: { staggerChildren: 0.1 } },
    }}
    className={className}
  >
    {children}
  </motion.div>
);

const StaggerItem = ({ children, className = "" }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 20 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" },
      },
    }}
    className={className}
  >
    {children}
  </motion.div>
);

/* --------------------------------------------------------------------------
    SUB-COMPONENT: FAQ ITEM
--------------------------------------------------------------------------- */
const FAQItem = ({ q, a, isOpen, onClick }) => {
  return (
    <div className="border-b border-black/10 last:border-0">
      <button
        onClick={onClick}
        aria-expanded={isOpen}
        className="w-full py-6 sm:py-8 flex justify-between items-start text-left group outline-none"
        style={{ WebkitTapHighlightColor: "transparent" }}
      >
        <h3 className={`text-lg sm:text-2xl font-medium tracking-tight pr-8 transition-colors duration-300 ${isOpen ? "text-black" : "text-black/80 group-hover:text-black"}`}>
            {q}
        </h3>
        <motion.div
            animate={{ rotate: isOpen ? 45 : 0 }}
            transition={{ duration: 0.3, ease: "circOut" }}
            className="flex-shrink-0 pt-1"
        >
          <Plus size={24} className={`transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-40 group-hover:opacity-100"}`} />
        </motion.div>
      </button>
      
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
                height: { duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }, 
                opacity: { duration: 0.25, delay: 0.1 } 
            }}
            className="overflow-hidden"
          >
            <div className="pb-8 text-base sm:text-lg text-black/60 font-light max-w-2xl leading-relaxed">
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* --------------------------------------------------------------------------
    COUNTER
--------------------------------------------------------------------------- */
const Counter = ({ value }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const motionValue = useMotionValue(0);
  const spring = useSpring(motionValue, { damping: 30, stiffness: 100 });
  const [display, setDisplay] = useState(0);

  const numeric = parseFloat(value.replace(/[^0-9.]/g, ""));
  const suffix = value.replace(/[0-9.]/g, "");

  useEffect(() => {
    if (inView) motionValue.set(numeric);
  }, [inView, numeric, motionValue]);

  useEffect(() => {
    const unsubscribe = spring.on("change", (v) => {
      setDisplay(Number.isInteger(numeric) ? Math.round(v) : v.toFixed(1));
    });
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [spring, numeric]);

  return (
    <span ref={ref} className="tabular-nums">
      {display}
      {suffix}
    </span>
  );
};

/* --------------------------------------------------------------------------
    PAGE COMPONENT
--------------------------------------------------------------------------- */
const HomePage = () => {
  const location = useLocation();
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  /* ------------------ Elegant Word Split ------------------ */
  const splitTitle = (text, delay = 0) =>
    text.split(" ").map((word, index) => (
      <motion.span
        key={index}
        initial={{ opacity: 0, y: 40, rotate: 2 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{
          duration: 0.8,
          delay: delay + index * 0.1,
          ease: [0.2, 0.65, 0.3, 0.9],
        }}
        className="inline-block mr-2 sm:mr-3 origin-top-left"
      >
        {word}
      </motion.span>
    ));

  /* ------------------ Minimal Section Header ------------------ */
  const SectionHeader = ({ title, subtitle, centered }) => (
    <FadeIn className={`mb-12 sm:mb-20 ${centered ? "text-center mx-auto" : ""}`}>
      <h2
        className={`text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tighter ${THEME.textMain} mb-4`}
      >
        {title}
      </h2>
      {subtitle && (
        <p className={`text-lg sm:text-xl ${THEME.textMuted} font-light max-w-xl ${centered ? "mx-auto" : ""}`}>
          {subtitle}
        </p>
      )}
    </FadeIn>
  );

  return (
    <>
      {/* 1. SEO METADATA (Fixes Page Title Error) */}

<Helmet>
    {/* --- STANDARD SEO --- */}
    <title>Creative Intelligence for Brands | [Framelync]</title>
    <meta name="description" content="We are a premium digital agency specializing in branding, web development, and motion graphics. Accelerate your business 10x faster." />
    <link rel="canonical" href="https://www.yourdomain.com/" />

    {/* --- SOCIAL MEDIA (OPEN GRAPH) --- */}
    <meta property="og:title" content="Creative Intelligence for Brands" />
    <meta property="og:description" content="Accelerate your business 10x faster with our premium design and dev services." />
    <meta property="og:image" content="https://www.yourdomain.com/images/og-share-image.jpg" /> {/* CREATE THIS IMAGE */}
    <meta property="og:url" content="https://www.yourdomain.com/" />
    <meta property="og:type" content="website" />

    {/* --- TWITTER CARDS --- */}
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content="Creative Intelligence for Brands" />
    <meta name="twitter:description" content="Premium branding and web development agency." />
    <meta name="twitter:image" content="https://www.yourdomain.com/images/og-share-image.jpg" />

    {/* --- SCHEMA MARKUP (JSON-LD) --- 
        This tells Google you are a professional organization 
    */}
    <script type="application/ld+json">
    {`
        {
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Framelync",
          "image": "https://www.framelync.com//logo-file-black.png",
          "url": "https://www.framelync.com",
          "telephone": "+91-7619160823",
          "address": {
            "@type": "Mysore",
            "streetAddress": "Mysore",
            "addressLocality": "Mysore",
            "postalCode": "570022",
            "addressCountry": "IN"
          },
          "priceRange": "$$$",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday"
            ],
            "opens": "09:00",
            "closes": "18:00"
          }
        }
    `}
    </script>
</Helmet>

      <main className={`min-h-screen ${THEME.bg} font-sans selection:bg-black selection:text-white relative`}>

        {/* BACKGROUND GRID */}
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808025_1px,transparent_1px),linear-gradient(to_bottom,#80808025_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
        </div>

        <div className="relative z-10">

        {/* ============================================================
            HERO SECTION
        ============================================================ */}
        <header className="relative pt-32 pb-12 sm:pt-40 sm:pb-24 text-center px-4 overflow-hidden">
          
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="flex justify-center mb-8"
          >
            <div className="px-4 py-1.5 rounded-full border border-black/10 flex items-center gap-2 bg-white/50 backdrop-blur-md">
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-black/60">
                 &bull; Est. 2025
              </span>
            </div>
          </motion.div>

          {/* 2. FIXED H1: Added aria-label. 
              Robots see "Creative Intelligence for Brands." 
              Humans see the animation.
          */}
          <h1 
            aria-label="Creative Intelligence for Brands."
            className={`text-4xl sm:text-7xl md:text-9xl font-semibold tracking-tighter ${THEME.textMain} leading-[0.9] mb-8`}
          >
            <span aria-hidden="true">
                {splitTitle("Creative Intelligence", 0.1)}
            </span>
            <br />
            <span aria-hidden="true" className="font-serif italic font-light opacity-80 block mt-2 sm:mt-4">
              {splitTitle("for Brands.", 0.4)}
            </span>
          </h1>

          <FadeIn delay={0.6}>
            <p className={`text-lg sm:text-2xl ${THEME.textMuted} max-w-2xl mx-auto leading-relaxed mb-12 font-normal px-4`}>
              Accelerate your business 10× faster.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 items-center w-full max-w-md mx-auto sm:max-w-none px-4">
              
              {/* 3. SEO FIX: Changed <button> to <Link>. 
                  Search engines follow links. They do not click buttons.
                  This fixes "Few internal links" and improves crawlability.
              */}
              <Link
                to="/loading"
                className={`group relative overflow-hidden ${THEME.buttonPrimary} w-full sm:w-auto px-10 py-4 rounded-full text-sm font-medium transition-all hover:scale-105 duration-300 inline-flex items-center justify-center`}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Get a Quote <ArrowUpRight size={16} />
                </span>
              </Link>

              <Link
                to="/our-works"
                className="w-full sm:w-auto px-10 py-4 rounded-full border border-black/10 text-black text-center text-sm font-medium hover:bg-white transition-all duration-300 flex items-center justify-center"
              >
                View Portfolio
              </Link>
            </div>
          </FadeIn>
        </header>

        {/* ============================================================
            CAROUSEL
        ============================================================ */}
        <section aria-label="Selected Works">
            <FadeIn delay={0.8} className="w-full max-w-[110rem] mx-auto mt-[-30px] sm:mt-[-60px] mb-24 relative z-10 px-0 sm:px-4">
            <Carousel />
            </FadeIn>
        </section>

        {/* ============================================================
            STATS
        ============================================================ */}
        <section className="w-full border-y border-black/5 py-16 sm:py-24 bg-white/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6">
            <StaggerContainer className="flex flex-wrap justify-center items-center gap-12 sm:gap-16 md:gap-32">
              {[
                { label: "Projects Done", value: "8+", icon: Briefcase },
                { label: "Clients", value: "4+", icon: Users },
                { label: "AVG. Client Rating", value: "9.8", icon: Trophy },
              ].map((s, i) => (
                <StaggerItem key={i} className="flex flex-col items-center justify-center text-center min-w-[140px]">
                  <div className="text-5xl sm:text-6xl md:text-7xl font-medium tracking-tighter mb-3">
                    <Counter value={s.value} />
                  </div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/40">
                    {s.label}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* ============================================================
            PARTNERS
        ============================================================ */}
        <section className="py-20 overflow-hidden">
          <div className="w-full">
            <Partners />
          </div>
        </section>

        {/* ============================================================
            SERVICES / CAPABILITIES (UPDATED)
        ============================================================ */}
        <section id="our-services" className="py-24 max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 md:mb-24 gap-6">
             <FadeIn>
              <h2 className="text-4xl sm:text-5xl md:text-7xl font-semibold tracking-tighter">Core Services.</h2>
             </FadeIn>
          </div>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
            {[
              {
                title: "Explainer Videos",
                desc: "Narrative precision meets motion.",
                benefits: "Simplify complex ideas into engaging visual stories. We help you increase conversion rates, train people faster, and explain products within few minutes.",
                icon: Video,
              },
              {
                title: "Branding",
                desc: "Create visual hierarchy for your business.",
                benefits: "Build a memorable identity that resonates. From logo systems to brand guidelines, we ensure consistency across all touchpoints to establish market trust.",
                icon: MonitorPlay,
              },
              {
                title: "Product Demos",
                desc: "Creative showcases to elevate experiences.",
                benefits: "Show, don't just tell. We highlight your product's key features through immersive motion graphics that drive excitement and accelerate user adoption.",
                icon: Share2,
              },
              {
                title: "Websites",
                desc: "Expand your digital presence to the world.",
                benefits: "Blazing fast, SEO-optimized, and responsive. We build scalable platforms designed to capture attention and guide visitors seamlessly toward becoming customers.",
                icon: Globe,
              },
            ].map((s, i) => (
              <StaggerItem key={i}>
                <div className="group relative border-t border-black/10 pt-8 transition-all hover:border-black/100 cursor-default">
                  <div className="absolute right-0 top-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform group-hover:translate-x-1">
                    <ArrowUpRight size={28} />
                  </div>
                  
                  <s.icon className="w-8 h-8 mb-6 text-black/70 group-hover:text-black transition-colors" strokeWidth={1.5} />
                  
                  <h3 className="text-3xl font-medium tracking-tight mb-3 group-hover:translate-x-2 transition-transform duration-300">
                    {s.title}
                  </h3>
                  
                  <p className="text-lg text-black/60 font-medium group-hover:text-black transition-colors">
                    {s.desc}
                  </p>

                  {/* Added "Why choose this" text block */}
                  <p className="mt-4 text-sm text-black/50 leading-relaxed font-light max-w-md">
                    {s.benefits}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </section>

        {/* ============================================================
            TESTIMONIALS
        ============================================================ */}
        <section className="py-24 bg-[#EBEAE5] border-y border-black/5">
          <div className="max-w-7xl mx-auto px-6">
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
              {[
                {
                  text: "The way you guys executed the work shows commitment and dedication. I am very happy with the output and will definitely recommend you.",
                  author: "Ravi Narayana Rao",
                  role: "Dhatri Financial Services",
                },
                {
                  text: "The quality of the work was excellent and the team was responsive. We are very happy with the final product.",
                  author: "Deepthi K",
                  role: "Ryich Delicacies",
                },
              ].map((t, i) => (
                <StaggerItem key={i} className="flex flex-col justify-between">
                  <div>
                    <span className="text-6xl font-serif text-black/10">“</span>
                    <blockquote className="text-xl sm:text-2xl md:text-3xl font-light leading-snug tracking-tight text-black mt-[-20px] mb-8">
                      {t.text}
                    </blockquote>
                  </div>
                  <div className="flex items-center gap-4">
                      <div className="h-px w-12 bg-black/20"></div>
                      <div>
                        <p className="font-semibold text-sm uppercase tracking-wide">{t.author}</p>
                        <p className="text-xs text-black/50">{t.role}</p>
                      </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* ============================================================
            FAQ SECTION
        ============================================================ */}
        <section className="py-24 max-w-4xl mx-auto px-6">
          <SectionHeader title="Questions." centered />

          <div className="space-y-0 mt-12">
            {[
              {
                q: "How long do typical projects take?",
                a: "Most sprints take 1-3 weeks depending on the complexity and scope of the deliverable.",
              },
              {
                q: "How does your pricing structure work?",
                a: "We offer flat-rate sprints. No hidden billing, no surprise hourly rates. You know exactly what you pay upfront.",
              },
              {
                q: "Do you work with international teams?",
                a: "Yes. Our workflow is async-first, designed for global collaboration across different time zones.",
              },
            ].map((f, i) => (
              <FAQItem 
                  key={i} 
                  q={f.q} 
                  a={f.a} 
                  isOpen={openFaq === i} 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>

          {/* CONTACT SUPPORT */}
            <div className="mt-20 text-center">
                  <p className="text-black/40 mb-4">Still have questions?</p>
                  <Link
                    to="/loading"
                    className="text-sm font-bold uppercase tracking-widest border-b border-black pb-1 hover:text-black/60 transition-colors"
                  >
                    Contact Support
                  </Link>
            </div>

            </section>
        
            </div> {/* End Relative Z-10 Wrapper */}
      </main>
    </>
  );
};

export default HomePage;